/**
 * Base CRUD Service
 *
 * This abstract class provides generic CRUD operations that can be
 * extended by domain-specific services to reduce code duplication.
 */

import {apiClient} from '../api/client';
import {ApiService} from '../api/service';
import {ApiResponse} from "../../../model";

/**
 * Generic CRUD service base class
 *
 * @template TId - The ID type (defaults to number)
 * @template TFilter - The filter type for search operations
 */
export abstract class CrudService extends ApiService {

    /**
     * The base endpoint path for this entity
     * Must be implemented by subclasses, e.g. '/api/committee'
     */
    protected abstract readonly basePath: string;

    /**
     * Generic method for custom post requests
     */
    protected async customPost<TRequest, TResponse>(
        endpoint: string,
        request: TRequest
    ): Promise<TResponse | null> {
        try {
            const response = await apiClient.post<ApiResponse<TResponse>>(
                `${this.basePath}/${endpoint}`,
                request
            );

            return this.processResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * Generic method for custom post requests
     */
    protected async customGet<TResponse>(
        endpoint: string
    ): Promise<TResponse | null> {
        try {
            const response = await apiClient.get<ApiResponse<TResponse>>(
                `${this.basePath}/${endpoint}`
            );

            return this.processResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    protected async customGetArraybuffer<TResponse>(endpoint: string): Promise<TResponse>{
        const response = await apiClient.get(
            `${this.basePath}/${endpoint}`,
            { responseType: 'arraybuffer' }
        );
        return response.data;
    }

    protected async customGetPdf(endpoint: string): Promise<any>{
        const response = await apiClient.get(
            `${this.basePath}/${endpoint}`,
            {
                responseType: 'blob',
                headers: {
                    'Accept': 'application/pdf'
                }
            }
        );

        const disposition = response.headers['content-disposition'];
        let filename = 'downloaded-file';
        if (disposition) {
            // 嘗試匹配 filename*
            const filenameRegex = /filename\*=UTF-8''([^;\n]*)/;
            const match = disposition.match(filenameRegex);

            if (match && match[1]) {
                filename = decodeURIComponent(match[1]);
            } else {
                // 若 filename* 不存在，則嘗試 filename
                const filenameFallbackRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const fallbackMatch = disposition.match(filenameFallbackRegex);
                if (fallbackMatch && fallbackMatch[1]) {
                    filename = fallbackMatch[1].replace(/['"]/g, '');
                }
            }
        }

        // 直接將 blob 保存為檔案
        const blob = new Blob([response.data], { type: 'application/pdf' });
        return {
            blob: blob,
            fileName: filename
        };
    }

    protected async customGetExcel(endpoint: string): Promise<any>{
        const response = await apiClient.get(
            `${this.basePath}/${endpoint}`,
            {
                responseType: 'blob',
                headers: {
                    'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                }
            }
        );

        const disposition = response.headers['content-disposition'];
        let filename = 'downloaded-file';
        if (disposition) {
            // 嘗試匹配 filename*
            const filenameRegex = /filename\*=UTF-8''([^;\n]*)/;
            const match = disposition.match(filenameRegex);

            if (match && match[1]) {
                filename = decodeURIComponent(match[1]);
            } else {
                // 若 filename* 不存在，則嘗試 filename
                const filenameFallbackRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const fallbackMatch = disposition.match(filenameFallbackRegex);
                if (fallbackMatch && fallbackMatch[1]) {
                    filename = fallbackMatch[1].replace(/['"]/g, '');
                }
            }
        }

        // 直接將 blob 保存為檔案
        const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        return {
            blob: blob,
            fileName: filename
        };
    }

    protected async customPostPdf<TRequest>(endpoint: string, request: TRequest): Promise<any>{
        const response = await apiClient.post(
            `${this.basePath}/${endpoint}`,
            request,
            {
                timeout: 300000,
                responseType: 'blob',
                headers: {
                    'Accept': 'application/pdf'
                }
            }
        );

        const disposition = response.headers['content-disposition'];
        let filename = 'downloaded-file';
        if (disposition) {
            // 嘗試匹配 filename*
            const filenameRegex = /filename\*=UTF-8''([^;\n]*)/;
            const match = disposition.match(filenameRegex);

            if (match && match[1]) {
                filename = decodeURIComponent(match[1]);
            } else {
                // 若 filename* 不存在，則嘗試 filename
                const filenameFallbackRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const fallbackMatch = disposition.match(filenameFallbackRegex);
                if (fallbackMatch && fallbackMatch[1]) {
                    filename = fallbackMatch[1].replace(/['"]/g, '');
                }
            }
        }

        // 直接將 blob 保存為檔案
        const blob = new Blob([response.data], { type: 'application/pdf' });
        return {
            blob: blob,
            fileName: filename
        };
    }

    protected async customPostExcel<TRequest>(endpoint: string, request: TRequest): Promise<any>{
        const response = await apiClient.post(
            `${this.basePath}/${endpoint}`,
            request,
            {
                timeout: 300000,
                responseType: 'blob',
                headers: {
                    'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                }
            }
        );
        const disposition = response.headers['content-disposition'];
        let filename = 'downloaded-file';
        if (disposition) {
            // 嘗試匹配 filename*
            const filenameRegex = /filename\*=UTF-8''([^;\n]*)/;
            const match = disposition.match(filenameRegex);

            if (match && match[1]) {
                filename = decodeURIComponent(match[1]);
            } else {
                // 若 filename* 不存在，則嘗試 filename
                const filenameFallbackRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const fallbackMatch = disposition.match(filenameFallbackRegex);
                if (fallbackMatch && fallbackMatch[1]) {
                    filename = fallbackMatch[1].replace(/['"]/g, '');
                }
            }
        }

        // 直接將 blob 保存為檔案
        const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        return {
            blob: blob,
            fileName: filename
        };
    }

    protected async customPostFile<TRequest>(endpoint: string, request: TRequest, fileType: 'csv' | 'txt', handleErrorLocally = false): Promise<any> {
        const response = await apiClient.post(
            `${this.basePath}/${endpoint}`,
            request,
            {
                responseType: 'blob',
                handleErrorLocally,
                headers: {
                    'Accept': `${fileType === 'csv' ? 'text/csv' : 'text/plain'}, application/json`
                }
            }
        );

        const disposition = response.headers['content-disposition'];
        let filename = fileType === 'csv' ? 'downloaded-file.csv' : 'downloaded-file.txt';
        if (disposition) {
            const filenameRegex = /filename\*=UTF-8''([^;\n]*)/;
            const match = disposition.match(filenameRegex);
            if (match && match[1]) {
                filename = decodeURIComponent(match[1]);
            } else {
                const filenameFallbackRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const fallbackMatch = disposition.match(filenameFallbackRegex);
                if (fallbackMatch && fallbackMatch[1]) {
                    filename = fallbackMatch[1].replace(/['"]/g, '');
                }
            }
        }

        const blob = new Blob([response.data], {
            type: fileType === 'csv' ? 'text/csv' : 'text/plain'
        });

        return {
            blob: blob,
            fileName: filename
        };
    }

}
